import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Stratiq i18n.
 *
 * Two helpers:
 *  - t("path.in.STRINGS")     → static UI labels (defined here)
 *  - cmsT(value)              → CMS field that may be string OR { es, en }
 *                               String → treated as Spanish (legacy data, no migration needed)
 */

const LANG_KEY = "stratiq_lang";

const STRINGS = {
  es: {
    nav: {
      services: "Servicios",
      portfolio: "Portafolio",
      about: "Nosotros",
      contact: "Contacto",
      cta: "Agendar diagnóstico",
    },
    hero: {
      ctaPrimary: "Agendar diagnóstico",
      ctaSecondary: "Ver servicios",
    },
    services: {
      eyebrow: "Servicios",
      title: "Nuestros servicios",
      subtitle: "Capacidades end-to-end para diagnosticar, diseñar e implementar mejora operativa con IA aplicada.",
    },
    stats: { eyebrow: "Resultados" },
    howWeWork: {
      eyebrow: "Metodología",
      title: "¿Cómo trabajamos?",
      steps: [
        { t: "Diagnóstico",    d: "Entendemos tu operación actual, identificamos cuellos de botella y mapeamos oportunidades de impacto inmediato." },
        { t: "Diseño",         d: "Definimos la solución óptima con arquitectura técnica, KPIs medibles y un plan de implementación claro." },
        { t: "Implementación", d: "Ejecutamos la solución, integramos con tu stack y medimos resultados en cada hito del proyecto." },
      ],
    },
    portfolio: {
      eyebrow: "Portafolio",
      title: "Casos de éxito",
      empty: "Sin casos publicados aún.",
    },
    about: {
      eyebrow: "Nosotros",
      title: "Quiénes somos",
      statusPill: "Aceptando proyectos · Q1 2026",
      coreCompetencies: "Core competencies",
      capabilitiesCard: "Capacidades Stratiq",
      legendActive: "Core capability",
      legendCount: "5 / 5 áreas activas",
      facts: {
        baseLabel:    "Base",
        baseValue:    "Querétaro, MX",
        reachLabel:   "Alcance",
        reachValue:   "LATAM · US",
        langLabel:    "Idiomas",
        langValue:    "ES · EN",
        modelLabel:   "Modelo",
        modelValue:   "Boutique",
      },
    },
    cta: {
      eyebrow: "Próximo paso",
      micro: "30 min · Sin compromiso · Plan accionable",
      button: "Agendar diagnóstico gratuito",
    },
    contact: {
      eyebrow: "Contacto",
      title: "Contáctanos",
      subtitle: "Comparte un poco sobre tu operación y te devolvemos una primera lectura en menos de 24h.",
      diagBadge: "Diagnóstico gratuito",
      diagText: "30 minutos. Sin compromiso. Salimos con un plan accionable.",
      placeholders: {
        name: "Nombre",
        company: "Empresa",
        email: "Email",
        message: "Cuéntanos sobre tu proyecto u operación",
      },
      submit: "Enviar mensaje",
      sending: "Enviando...",
      success: "Mensaje enviado. Te contactaremos pronto.",
      errorMissing: "Por favor completa nombre, email y mensaje.",
      errorGeneric: "No se pudo enviar el mensaje. Intenta de nuevo.",
      info: { email: "Email", whatsapp: "WhatsApp", responseTime: "Tiempo respuesta" },
      serviceOptions: ["Consultoría BA", "Automatización", "Plataformas", "Soluciones AI", "Manuales y Procesos"],
      location: {
        section: "Sede",
        coords: "Coordenadas",
        available: "Disponible para reuniones",
      },
    },
    footer: {
      systemOk: "System operational",
      tagline: "AI & Business Advisory",
      builtWith: "Built with intent",
      copyright: "© 2025 Stratiq · Querétaro, MX",
      siteLabel: "Sitio",
    },
    meta: {
      description: "Stratiq — AI & Business Advisory. Automatizamos procesos, desarrollamos plataformas empresariales y aplicamos inteligencia artificial para que tu empresa escale con eficiencia.",
    },
  },
  en: {
    nav: {
      services: "Services",
      portfolio: "Portfolio",
      about: "About",
      contact: "Contact",
      cta: "Book a Call",
    },
    hero: {
      ctaPrimary: "Book a free call",
      ctaSecondary: "View services",
    },
    services: {
      eyebrow: "Services",
      title: "Our Services",
      subtitle: "End-to-end capabilities to diagnose, design, and implement operational improvement with applied AI.",
    },
    stats: { eyebrow: "Results" },
    howWeWork: {
      eyebrow: "Methodology",
      title: "How We Work",
      steps: [
        { t: "Diagnosis",      d: "We understand your current operation, identify bottlenecks, and map immediate-impact opportunities." },
        { t: "Design",         d: "We define the optimal solution with technical architecture, measurable KPIs, and a clear implementation plan." },
        { t: "Implementation", d: "We execute the solution, integrate with your stack, and measure results at every project milestone." },
      ],
    },
    portfolio: {
      eyebrow: "Portfolio",
      title: "Case Studies",
      empty: "No case studies published yet.",
    },
    about: {
      eyebrow: "About",
      title: "Who We Are",
      statusPill: "Now accepting projects · Q1 2026",
      coreCompetencies: "Core competencies",
      capabilitiesCard: "Stratiq capabilities",
      legendActive: "Core capability",
      legendCount: "5 / 5 active areas",
      facts: {
        baseLabel:    "Based in",
        baseValue:    "Querétaro, MX",
        reachLabel:   "Reach",
        reachValue:   "LATAM · US",
        langLabel:    "Languages",
        langValue:    "EN · ES",
        modelLabel:   "Model",
        modelValue:   "Boutique",
      },
    },
    cta: {
      eyebrow: "Next step",
      micro: "30 min · No commitment · Actionable plan",
      button: "Book a free diagnostic",
    },
    contact: {
      eyebrow: "Contact",
      title: "Contact Us",
      subtitle: "Share a bit about your operation and we'll get back to you within 24h with an initial assessment.",
      diagBadge: "Free diagnostic",
      diagText: "30 minutes. No commitment. You leave with an actionable plan.",
      placeholders: {
        name: "Name",
        company: "Company",
        email: "Email",
        message: "Tell us about your project or operation",
      },
      submit: "Send message",
      sending: "Sending...",
      success: "Message sent. We'll be in touch shortly.",
      errorMissing: "Please complete name, email, and message.",
      errorGeneric: "Could not send the message. Please try again.",
      info: { email: "Email", whatsapp: "WhatsApp", responseTime: "Response time" },
      serviceOptions: ["BA Consulting", "Process Automation", "Platform Development", "AI Solutions", "Manuals & Processes"],
      location: {
        section: "Headquarters",
        coords: "Coordinates",
        available: "Available for meetings",
      },
    },
    footer: {
      systemOk: "System operational",
      tagline: "AI & Business Advisory",
      builtWith: "Built with intent",
      copyright: "© 2025 Stratiq · Querétaro, MX",
      siteLabel: "Site",
    },
    meta: {
      description: "Stratiq — AI & Business Advisory. We automate processes, build enterprise platforms, and apply artificial intelligence to help companies scale efficiently.",
    },
  },
};

const LangContext = createContext({
  lang: "es",
  setLang: () => {},
  t: () => "",
  cmsT: () => "",
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) === "en" ? "en" : "es"; }
    catch { return "es"; }
  });

  const setLang = useCallback((l) => {
    const next = l === "en" ? "en" : "es";
    setLangState(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", STRINGS[lang].meta.description);
  }, [lang]);

  const t = useCallback((path) => {
    const parts = path.split(".");
    let v = STRINGS[lang];
    for (const p of parts) {
      if (v == null) return "";
      v = v[p];
    }
    return v == null ? "" : v;
  }, [lang]);

  const cmsT = useCallback((value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value[lang] || value.es || value.en || "";
    return String(value);
  }, [lang]);

  const ctx = useMemo(() => ({ lang, setLang, t, cmsT }), [lang, setLang, t, cmsT]);

  return <LangContext.Provider value={ctx}>{children}</LangContext.Provider>;
}

export function useI18n() {
  return useContext(LangContext);
}
